import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { PageQuery, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { NameDto } from '@model/NameDto'
import { TradingAccountRebateDetailedDto } from '@model/TradingAccountRebateDetailedDto'

import { FilterQueryProps } from '@global/filter/FilterQueryModal'

import { useCallbackWithForceRefresh } from '@hooks/useCallbackWithForceRefresh'
import { IAppendableFetchResult, useFetchAppendablePage } from '@hooks/useFetch'

interface IBTradingAccountsRebate {
  query: IAppendableFetchResult<TradingAccountRebateDetailedDto[]>
  sortOptions: NameDto<TradingAccountsRebateSortOptions | null>[]
  search?: FilterQueryProps
  setSearch: (search: FilterQueryProps) => void
  forceRefresh: () => void
  clearSearch: (callback: () => void) => void
}

export type TradingAccountsRebateSortOptions =
  | 'TradingAccountId'
  | 'TradingAccountName'
  | 'TradingAccountTypeId'
  | 'TradingAccountTypeName'
  | 'TradingAccountSignedUpDate'
  | 'ClientId'
  | 'ClientName'
  | 'CountryId'
  | 'CountryName'
  | 'RebateGroupId'
  | 'RebateGroupName'
  | 'TotalVolumeFxm'
  | 'TotalVolumeOther'
  | 'TotalCommission'
  | 'RebateAmountInPercentage'

export const useIBTradingAccountsRebate = (): IBTradingAccountsRebate => {
  const { t } = useTranslation()

  const apiClient = useApiClient(ClientApiClient)
  const locale = useSessionLanguage()
  const { account } = useAccountReadContext()

  const [search, setSearch] = useState<FilterQueryProps>()

  const sortOptions: NameDto<TradingAccountsRebateSortOptions | null>[] = [
    {
      id: 'TradingAccountName',
      name: t('IB.RebatesPage.ClientsPage.Trading account'),
    },
    {
      id: 'ClientName',
      name: t('IB.RebatesPage.ClientsPage.Client Name'),
    },
    {
      id: 'TradingAccountTypeName',
      name: t('IB.RebatesPage.ClientsPage.Account Type'),
    },
    {
      id: 'TradingAccountSignedUpDate',
      name: t('IB.RebatesPage.Signed up on'),
    },
    {
      id: 'CountryName',
      name: t('IB.RebatesPage.ClientsPage.Country'),
    },
    {
      id: 'RebateGroupName',
      name: t('IB.RebatesPage.ClientsPage.Group'),
    },
    {
      id: 'TotalVolumeFxm',
      name: t('IB.RebatesPage.ClientsPage.Vol. FXM'),
    },
    {
      id: 'TotalVolumeOther',
      name: t('IB.RebatesPage.ClientsPage.Vol. Other'),
    },
    {
      id: 'TotalCommission',
      name: t('IB.RebatesPage.ClientsPage.Commission'),
    },
    {
      id: 'RebateAmountInPercentage',
      name: t('IB.RebatesPage.ClientsPage.Rebate'),
    },
  ]

  const clearSearch = (callback: () => void) => {
    callback()
    setSearch({})
  }

  const { callback: accountSummaryCallback, forceRefresh } = useCallbackWithForceRefresh(
    async (query?: PageQuery) => {
      return await apiClient.getIBTradingAccountsRebate(
        account?.clientIntroducingBroker?.id ?? '',
        {
          ...query,
          search: search,
          caller: 'ca',
          languageId: locale,
        }
      )
    },
    [search, locale]
  )
  const query = useFetchAppendablePage(accountSummaryCallback)

  return {
    query,
    sortOptions,
    search,
    setSearch,
    forceRefresh,
    clearSearch,
  }
}
